import { useEffect, useRef, useState } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  Container
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

export default function TestimonialLeadForm({ 
  testimonial = "This is one of the best resources I've come across in a long time...",
  title = "Get lifetime access to our exclusive salary resource library", 
  subtitle = "The best salary calculators, guides, and templates. You'll get immediate access to 25+ resources you won't find elsewhere.",
  buttonText = "Get access now",
  consentText = "By submitting, you agree to receive emails from SalaryTools."
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    // Skip Turnstile in development mode to avoid domain validation errors
    if (process.env.NODE_ENV === 'development') {
      setIsFormReady(true);
      return;
    }

    if (!TURNSTILE_SITE_KEY) {
      setIsFormReady(true);
      return;
    }

    // Set a timeout to show form even if Turnstile fails to load
    const formReadyTimeout = setTimeout(() => {
      setIsFormReady(true);
    }, 2000);

    const renderTurnstile = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current) return;
      
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => {
            setToken(token);
            setIsFormReady(true);
            clearTimeout(formReadyTimeout);
          },
          "expired-callback": () => setToken(""),
          "error-callback": () => {
            setToken("");
            setIsFormReady(true);
            clearTimeout(formReadyTimeout);
          }
        });
        // Set form ready even if callback doesn't fire immediately
        setTimeout(() => {
          setIsFormReady(true);
          clearTimeout(formReadyTimeout);
        }, 500);
      } catch (error) {
        setIsFormReady(true);
        clearTimeout(formReadyTimeout);
      }
    };

    // Add a small delay to ensure DOM is ready
    const initTurnstile = () => {
      if (window.turnstile) {
        // Small delay to ensure containerRef is mounted
        setTimeout(renderTurnstile, 100);
      } else {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Small delay after script loads to ensure DOM is ready
          setTimeout(renderTurnstile, 100);
        };
        document.head.appendChild(script);
      }
    };

    // Use requestAnimationFrame to ensure DOM is painted
    requestAnimationFrame(initTurnstile);

    return () => {
      clearTimeout(formReadyTimeout);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (error) {
          // Silently handle cleanup errors
        }
      }
    };
  }, [TURNSTILE_SITE_KEY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!gdprConsent) {
      setStatus("error");
      setMsg("Please accept the GDPR consent to continue.");
      return;
    }

    // Validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }

    // Check Turnstile in production
    if (process.env.NODE_ENV === 'production' && TURNSTILE_SITE_KEY && !token) {
      setStatus("error");
      setMsg("Please complete the verification.");
      return;
    }

    setStatus("submitting");
    setMsg("");

    try {
      // In development, simulate successful submission
      if (process.env.NODE_ENV === 'development') {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        setStatus("success");
        setMsg("Development mode: Form submission simulated successfully!");
        setEmail("");
        setGdprConsent(false);
        return;
      }

      const payload = {
        email,
        turnstileToken: token,
        gdprConsent: true,
        gdprTimestamp: new Date().toISOString(),
        source: 'testimonial-leadform'
      };

      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMsg(data.message || "Thank you! Check your email for your resources.");
        setEmail("");
        setGdprConsent(false);
        
        // Reset Turnstile
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setToken("");
        }
      } else {
        setStatus("error");
        setMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #e91e63 0%, #9c27b0 100%)',
        color: 'white',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        border: 'none',
        borderBottom: '1px solid #333',
        margin: 0,
        padding: 0
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -40,
          left: -40,
          width: 160,
          height: 160,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
        }}
      />

      <Container maxWidth="lg">
        <Box sx={{ py: 6, px: 4, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: 6
            }}
          >
            {/* Content Section */}
            <Box sx={{ flex: 1, maxWidth: { md: '55%' } }}>
              {/* Testimonial */}
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 4,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  lineHeight: 1.2,
                  fontStyle: 'italic'
                }}
              >
                "{testimonial}"
              </Typography>

              {/* Value Proposition */}
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.4,
                  opacity: 0.95
                }}
              >
                {title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  opacity: 0.9
                }}
              >
                {subtitle}
              </Typography>

              {/* Visual Elements */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 2,
                  mb: 4
                }}
              >
  
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    px: 2,
                    py: 1
                  }}
                >
                  <EmailIcon sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Instant Access
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Form Section */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { md: '400px' },
                width: '100%'
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.98)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}
              >
                {/* Hidden Turnstile container - always available for ref */}
                {TURNSTILE_SITE_KEY && process.env.NODE_ENV !== 'development' && (
                  <div ref={containerRef} style={{ display: isFormReady ? 'block' : 'none', textAlign: 'center', marginBottom: '1rem' }} />
                )}
                
                {!isFormReady ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                    <CircularProgress sx={{ color: '#e91e63' }} />
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Your email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={status === "submitting"}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        '&:hover fieldset': {
                          borderColor: '#e91e63',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#e91e63',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#e91e63',
                      },
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gdprConsent}
                        onChange={(e) => setGdprConsent(e.target.checked)}
                        disabled={status === "submitting"}
                        sx={{
                          color: '#666',
                          '&.Mui-checked': {
                            color: '#e91e63',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {consentText}{' '}
                        <Link 
                          href="/privacy-policy" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: '#e91e63' }}
                        >
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                    sx={{ mb: 3, alignItems: 'flex-start' }}
                  />


                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={status === "submitting"}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #e91e63 0%, #9c27b0 100%)',
                      boxShadow: '0 4px 20px rgba(233, 30, 99, 0.4)',
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #d81b60 0%, #8e24aa 100%)',
                        boxShadow: '0 6px 25px rgba(233, 30, 99, 0.5)',
                      },
                      '&:disabled': {
                        background: '#ccc',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {status === "submitting" ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Processing...
                      </>
                    ) : (
                      buttonText
                    )}
                  </Button>

                  {/* Status Messages */}
                  {msg && (
                    <Alert
                      severity={status === "success" ? "success" : "error"}
                      sx={{ 
                        mt: 2,
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          fontSize: '0.9rem'
                        }
                      }}
                    >
                      {msg}
                    </Alert>
                  )}
                </form>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Container>
    </Paper>
  );
}